import * as React from "react";

import {CookieCategory} from "@khan/data-cookies";
import {LocalStore} from "@khan/util-local-store";

import Expandable from "./expandable.tsx";
import {NavSubsectionContext} from "./nav-subsection-context.tsx";
import ToggleButton from "./toggle-button.tsx";

interface Props {
    // The section contents. Can be expanded or collapsed by this component.
    children: React.ReactNode;
    // The header label.
    label: React.ReactNode;
    // If true, the subsection will be collapsed when the component mounts.
    // Otherwise, the subsection will be expanded when the component mounts.
    initiallyCollapsed?: boolean;
    // Unique Local Storage id for this subsection for storing the state
    // of whether the subsection is open or closed
    localStorageId: string;
    triggerTestId?: string;
    contentTestId?: string;
}

const LOCAL_STORAGE_KEY = "navSubsectionState";

const getNavSubsectionState = (
    localStorageId: string,
    defaultValue: boolean,
): boolean => {
    const state = LocalStore.get(LOCAL_STORAGE_KEY);
    if (!state) {
        return defaultValue;
    }
    return typeof state[localStorageId] !== "boolean"
        ? defaultValue
        : state[localStorageId];
};

const setNavSubsectionState = (
    localStorageId: string,
    value: boolean,
): void => {
    const state: any = LocalStore.get(LOCAL_STORAGE_KEY) || {};
    state[localStorageId] = value;
    LocalStore.set(LOCAL_STORAGE_KEY, state, CookieCategory.Functional);
};

/**
 * A second-level navigation section, to be used inside a NavSection.
 * Expands/collapses its contents.
 */
const NavSubsection = (props: Props): React.ReactElement => {
    const {
        children,
        label,
        initiallyCollapsed,
        localStorageId,
        triggerTestId,
        contentTestId,
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(() =>
        getNavSubsectionState(localStorageId, !initiallyCollapsed),
    );

    // Sync state with local storage when localStorageId or initiallyCollapsed changes
    React.useEffect(() => {
        const expandedState = getNavSubsectionState(
            localStorageId,
            !initiallyCollapsed,
        );
        setIsExpanded(expandedState);
    }, [localStorageId, initiallyCollapsed]);

    const handleClickButton = (): void => {
        setNavSubsectionState(localStorageId, !isExpanded);
        setIsExpanded(!isExpanded);
    };

    return (
        <li>
            <ToggleButton
                label={label}
                isExpanded={isExpanded}
                onClick={handleClickButton}
                testId={triggerTestId}
            />
            <Expandable isExpanded={isExpanded}>
                <ul data-testid={contentTestId}>
                    <NavSubsectionContext.Provider
                        value={{isInSubsection: true}}
                    >
                        {children}
                    </NavSubsectionContext.Provider>
                </ul>
            </Expandable>
        </li>
    );
};

export default NavSubsection;
